import { useState } from 'react';
import { AlertTriangle, Trash2, Copy, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ParsedDiagram, ParsedPath, ParsedEntity } from '@/services/threatcatService';

interface Tm7ResolveDialogProps {
  open: boolean;
  diagrams: ParsedDiagram[];
  onImport: (resolved: ParsedDiagram[]) => void;
  onCancel: () => void;
  importing: boolean;
}

/**
 * For each diagram that has unresolved paths, shows a collapsible section
 * with a table where the user can assign missing source/target entities
 * or discard individual paths.
 */
export default function Tm7ResolveDialog({ open, diagrams, onImport, onCancel, importing }: Tm7ResolveDialogProps) {
  // Overrides for original unresolved paths: key = "di:pi"
  type Override = { source_id?: number; target_id?: number; discarded?: boolean };
  const [overrides, setOverrides] = useState<Record<string, Override>>({});

  // Cloned rows: key = "di:pi", value = array of clone overrides
  // Each clone inherits the original path but lets user pick a different endpoint
  const [clones, setClones] = useState<Record<string, Override[]>>({});

  const diagramsWithIssues = diagrams
    .map((d, i) => ({ diagram: d, index: i }))
    .filter(({ diagram }) => diagram.unresolved_paths.length > 0);

  const [expanded, setExpanded] = useState<Set<number>>(
    () => new Set(diagramsWithIssues.map(({ index }) => index))
  );

  const k = (di: number, pi: number) => `${di}:${pi}`;

  const getOverride = (di: number, pi: number): Override => overrides[k(di, pi)] || {};

  const setOverride = (di: number, pi: number, patch: Partial<Override>) => {
    setOverrides((prev) => ({ ...prev, [k(di, pi)]: { ...prev[k(di, pi)], ...patch } }));
  };

  const getClones = (di: number, pi: number): Override[] => clones[k(di, pi)] || [];

  const addClone = (di: number, pi: number, base: ParsedPath) => {
    const key = k(di, pi);
    setClones((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), { source_id: base.source_id ?? undefined, target_id: base.target_id ?? undefined }],
    }));
  };

  const setCloneOverride = (di: number, pi: number, ci: number, patch: Partial<Override>) => {
    const key = k(di, pi);
    setClones((prev) => {
      const arr = [...(prev[key] || [])];
      arr[ci] = { ...arr[ci], ...patch };
      return { ...prev, [key]: arr };
    });
  };

  const removeClone = (di: number, pi: number, ci: number) => {
    const key = k(di, pi);
    setClones((prev) => {
      const arr = [...(prev[key] || [])];
      arr.splice(ci, 1);
      return { ...prev, [key]: arr };
    });
  };

  const toggleExpanded = (idx: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  // Validate: all non-discarded originals + all clones must have both endpoints
  const allResolved = diagramsWithIssues.every(({ diagram, index: di }) =>
    diagram.unresolved_paths.every((p, pi) => {
      const ov = getOverride(di, pi);
      if (ov.discarded) return true;
      const srcOk = p.source_id != null || (ov.source_id != null && ov.source_id > 0);
      const tgtOk = p.target_id != null || (ov.target_id != null && ov.target_id > 0);
      if (!srcOk || !tgtOk) return false;
      // Check clones too
      return getClones(di, pi).every((c) => {
        const cSrc = p.source_id != null || (c.source_id != null && c.source_id > 0);
        const cTgt = p.target_id != null || (c.target_id != null && c.target_id > 0);
        return cSrc && cTgt;
      });
    })
  );

  const handleImport = () => {
    const resolved = diagrams.map((diagram, di) => {
      const extraPaths: ParsedPath[] = [];
      for (let pi = 0; pi < diagram.unresolved_paths.length; pi++) {
        const ov = getOverride(di, pi);
        if (ov.discarded) continue;
        const p = diagram.unresolved_paths[pi];
        // Original row
        extraPaths.push({
          ...p,
          source_id: ov.source_id ?? p.source_id,
          target_id: ov.target_id ?? p.target_id,
        });
        // Cloned rows
        for (const c of getClones(di, pi)) {
          if (c.discarded) continue;
          extraPaths.push({
            ...p,
            id: p.id + 100000 + extraPaths.length, // unique id for the clone
            source_id: c.source_id ?? p.source_id,
            target_id: c.target_id ?? p.target_id,
          });
        }
      }
      const { unresolved_paths: _, ...rest } = diagram;
      return { ...rest, paths: [...rest.paths, ...extraPaths], unresolved_paths: [] };
    });
    onImport(resolved);
  };

  const eName = (entities: ParsedEntity[], id: number | null | undefined) => {
    if (id == null) return null;
    return entities.find((e) => e.id === id)?.name ?? `#${id}`;
  };

  const totalUnresolved = diagramsWithIssues.reduce((s, { diagram }) => s + diagram.unresolved_paths.length, 0);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !importing) onCancel(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Unresolved connections
          </DialogTitle>
          <DialogDescription>
            {totalUnresolved} connection{totalUnresolved !== 1 ? 's' : ''} across {diagramsWithIssues.length} diagram{diagramsWithIssues.length !== 1 ? 's' : ''} have
            disconnected endpoints (highlighted in amber). Assign the correct entity or discard them.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 space-y-3 pr-1">
          {diagramsWithIssues.map(({ diagram, index: di }) => (
            <div key={di} className="rounded-md border">
              {/* Diagram header */}
              <button
                type="button"
                onClick={() => toggleExpanded(di)}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium hover:bg-accent/50 transition-colors"
              >
                {expanded.has(di)
                  ? <ChevronDown className="h-4 w-4 shrink-0" />
                  : <ChevronRight className="h-4 w-4 shrink-0" />}
                <span className="truncate">{diagram.name || `Diagram ${di + 1}`}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {diagram.unresolved_paths.length} unresolved
                </span>
              </button>

              {/* Path table — context rows + unresolved rows with dropdowns */}
              {expanded.has(di) && (() => {
                // Collect the entity ids that appear as the known side of unresolved paths
                // so we can show only RELEVANT resolved paths as context.
                const contextIds = new Set<number>();
                diagram.unresolved_paths.forEach((p) => {
                  // source missing → target is the known side
                  if (p.source_id == null && p.target_id != null) contextIds.add(p.target_id);
                  // target missing → source is the known side
                  if (p.target_id == null && p.source_id != null) contextIds.add(p.source_id);
                });
                // Resolved paths that share that endpoint
                const contextPaths = diagram.paths.filter(
                  (p) => contextIds.has(p.source_id!) || contextIds.has(p.target_id!)
                );

                return (
                <div className="border-t">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-3 py-1.5 font-medium">Connection</th>
                        <th className="text-left px-3 py-1.5 font-medium">Protocol</th>
                        <th className="text-left px-3 py-1.5 font-medium">Source</th>
                        <th className="text-left px-3 py-1.5 font-medium">Target</th>
                        <th className="w-8" />
                      </tr>
                    </thead>
                    <tbody>
                      {/* Context paths — resolved, read-only, sharing an endpoint with unresolved */}
                      {contextPaths.map((path) => (
                        <tr key={`r-${path.id}`} className="border-t text-muted-foreground">
                          <td className="px-3 py-2 max-w-[160px] truncate">{path.name || '(unnamed)'}</td>
                          <td className="px-3 py-2">{path.protocol || '—'}</td>
                          <td className="px-3 py-2 truncate max-w-[140px]">{eName(diagram.entities, path.source_id) ?? '—'}</td>
                          <td className="px-3 py-2 truncate max-w-[140px]">{eName(diagram.entities, path.target_id) ?? '—'}</td>
                          <td />
                        </tr>
                      ))}
                      {/* Unresolved paths — editable + clones */}
                      {diagram.unresolved_paths.map((path, pi) => {
                        const ov = getOverride(di, pi);
                        const pathClones = getClones(di, pi);
                        if (ov.discarded) {
                          return (
                            <tr key={`u-${path.id}`} className="border-t opacity-40 bg-amber-500/5">
                              <td className="px-3 py-2 line-through" colSpan={4}>{path.name || '(unnamed)'}</td>
                              <td className="px-2 py-2">
                                <button
                                  type="button"
                                  title="Restore"
                                  onClick={() => setOverride(di, pi, { discarded: false })}
                                  className="text-muted-foreground hover:text-foreground"
                                >↩</button>
                              </td>
                            </tr>
                          );
                        }
                        return (<>
                          {/* Original unresolved row */}
                          <tr key={`u-${path.id}`} className="border-t bg-amber-500/10">
                            <td className="px-3 py-2 font-medium max-w-[160px] truncate">
                              <span className="inline-flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" />
                                {path.name || '(unnamed)'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">{path.protocol || '—'}</td>
                            <td className="px-3 py-2">
                              <EndpointCell
                                resolvedId={path.source_id}
                                overrideId={ov.source_id}
                                entities={diagram.entities}
                                eName={eName}
                                onChange={(id) => setOverride(di, pi, { source_id: id })}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <EndpointCell
                                resolvedId={path.target_id}
                                overrideId={ov.target_id}
                                entities={diagram.entities}
                                eName={eName}
                                onChange={(id) => setOverride(di, pi, { target_id: id })}
                              />
                            </td>
                            <td className="px-2 py-2">
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  title="Duplicate row"
                                  onClick={() => addClone(di, pi, path)}
                                  className="text-muted-foreground hover:text-foreground transition-colors"
                                >
                                  <Copy className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  title="Discard"
                                  onClick={() => setOverride(di, pi, { discarded: true })}
                                  className="text-muted-foreground hover:text-destructive transition-colors"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {/* Clone rows */}
                          {pathClones.map((clone, ci) => (
                            <tr key={`c-${path.id}-${ci}`} className="border-t bg-amber-500/5">
                              <td className="px-3 py-2 text-muted-foreground max-w-[160px] truncate pl-7">
                                ↳ {path.name || '(unnamed)'}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground">{path.protocol || '—'}</td>
                              <td className="px-3 py-2">
                                <EndpointCell
                                  resolvedId={path.source_id}
                                  overrideId={clone.source_id}
                                  entities={diagram.entities}
                                  eName={eName}
                                  onChange={(id) => setCloneOverride(di, pi, ci, { source_id: id })}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <EndpointCell
                                  resolvedId={path.target_id}
                                  overrideId={clone.target_id}
                                  entities={diagram.entities}
                                  eName={eName}
                                  onChange={(id) => setCloneOverride(di, pi, ci, { target_id: id })}
                                />
                              </td>
                              <td className="px-2 py-2">
                                <button
                                  type="button"
                                  title="Remove clone"
                                  onClick={() => removeClone(di, pi, ci)}
                                  className="text-muted-foreground hover:text-destructive transition-colors"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </>);
                      })}
                    </tbody>
                  </table>
                </div>
                );
              })()}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={importing}>Cancel</Button>
          <Button onClick={handleImport} disabled={!allResolved || importing}>
            {importing
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing…</>
              : `Import ${diagrams.length} diagram${diagrams.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Renders either a static entity name (resolved) or a dropdown (unresolved). */
function EndpointCell({
  resolvedId,
  overrideId,
  entities,
  eName,
  onChange,
}: {
  resolvedId: number | null;
  overrideId: number | undefined;
  entities: ParsedEntity[];
  eName: (entities: ParsedEntity[], id: number | null | undefined) => string | null;
  onChange: (id: number) => void;
}) {
  if (resolvedId != null) {
    return <span className="truncate max-w-[140px] inline-block">{eName(entities, resolvedId) ?? '—'}</span>;
  }

  return (
    <select
      value={overrideId ?? ''}
      onChange={(e) => onChange(Number(e.target.value))}
      className={cn(
        'w-full rounded border bg-background px-2 py-1 text-xs',
        !overrideId && 'border-amber-400 text-amber-600'
      )}
    >
      <option value="">— select entity —</option>
      {entities.map((ent) => (
        <option key={ent.id} value={ent.id}>
          {ent.name || `Entity #${ent.id}`}
        </option>
      ))}
    </select>
  );
}
